import type maplibregl from "maplibre-gl";
import { createElement, useCallback, useEffect, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import { getMaplibre, loadMaplibre } from "@/lib/maplibre-lib";
import {
  createEventPinElement,
  updateEventPinElement,
} from "@/components/event-pin";
import { StationPin } from "@/components/station-pin";
import { getBottomSheetBottomInset } from "@/components/bottom-sheet";
import { taxiDarkNavyStyle } from "@/lib/taxi-map-style";
import type {
  Location,
  RouteOption,
  Station,
  Event as TrotroEvent,
} from "@/lib/api";

const ACCRA_CENTER: [number, number] = [-0.187, 5.6037];
const DEFAULT_ZOOM = 12;
const STATION_MIN_ZOOM = 14;
const STATION_LABEL_MIN_ZOOM = 16.2;
const COMPACT_PIN_ZOOM_THRESHOLD = 13;

const ANT_DASH_SEQUENCE: number[][] = [
  [0, 4, 3],
  [0.5, 4, 2.5],
  [1, 4, 2],
  [1.5, 4, 1.5],
  [2, 4, 1],
  [2.5, 4, 0.5],
  [3, 4, 0],
  [0, 0.5, 3, 3.5],
  [0, 1, 3, 3],
  [0, 1.5, 3, 2.5],
  [0, 2, 3, 2],
  [0, 2.5, 3, 1.5],
  [0, 3, 3, 1],
  [0, 3.5, 3, 0.5],
];

export type MapStyleName = "standard" | "satellite";

const SATELLITE_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    "esri-satellite": {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      maxzoom: 19,
    },
    "ofm-labels": {
      type: "vector",
      url: "https://tiles.openfreemap.org/planet",
    },
  },
  layers: [
    {
      id: "satellite-base",
      type: "raster",
      source: "esri-satellite",
    },
    {
      id: "ofm-road-label",
      type: "symbol",
      source: "ofm-labels",
      "source-layer": "transportation_name",
      minzoom: 12,
      layout: {
        "text-field": ["coalesce", ["get", "name:en"], ["get", "name"]],
        "text-size": ["interpolate", ["linear"], ["zoom"], 12, 10, 16, 13],
        "text-font": ["Noto Sans Regular"],
        "symbol-placement": "line",
        "text-max-angle": 30,
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-color": "#000000",
        "text-halo-width": 1.5,
      },
    },
    {
      id: "ofm-place-label",
      type: "symbol",
      source: "ofm-labels",
      "source-layer": "place",
      minzoom: 8,
      layout: {
        "text-field": ["coalesce", ["get", "name:en"], ["get", "name"]],
        "text-size": ["interpolate", ["linear"], ["zoom"], 8, 10, 12, 13, 14, 16],
        "text-font": ["Noto Sans Bold"],
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-color": "#000000",
        "text-halo-width": 2,
      },
    },
  ],
  glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
};

export function resolveMapStyle(style: MapStyleName): maplibregl.StyleSpecification {
  if (style === "satellite") {
    return SATELLITE_STYLE;
  }

  return taxiDarkNavyStyle;
}

type MarkerType = "origin" | "destination" | null;

interface EventMarkerData {
  marker: maplibregl.Marker;
  event: TrotroEvent;
  element: HTMLDivElement;
  isCompact: boolean;
}

interface StationMarkerData {
  marker: maplibregl.Marker;
  station: Station;
  element: HTMLDivElement;
  root: Root;
  onClick: () => void;
  renderState: {
    isSelected: boolean;
    showLabel: boolean;
    isVisible: boolean;
  };
}

function renderStationPin(
  root: Root,
  station: Station,
  isSelected: boolean,
  onClick: () => void,
  showLabel: boolean,
  isVisible: boolean,
): void {
  root.render(
    createElement(StationPin, {
      station,
      isSelected,
      onClick,
      showLabel,
      isVisible,
    }),
  );
}


function getStationGridSize(zoom: number): number {
  if (zoom < STATION_MIN_ZOOM) return Infinity;
  if (zoom >= 18) return 28;
  if (zoom >= 17) return 48;
  if (zoom >= 16) return 72;
  if (zoom >= 15) return 110;
  if (zoom >= 14.5) return 160;
  if (zoom >= 14) return 220;
  if (zoom >= 13.75) return 280;
  return 340;
}

function shouldShowStationLabel(zoom: number, isSelected: boolean): boolean {
  return isSelected || zoom >= STATION_LABEL_MIN_ZOOM;
}

function getMarkerInnerHTML(color: string, isSelected: boolean): string {
  const gradientEnd = color === "#3b82f6" ? "#2563eb" : "#dc2626";
  const selectedRing = isSelected
    ? `<div style="position:absolute;top:-6px;left:-6px;width:44px;height:44px;border:3px solid ${color};border-radius:50%;opacity:0.5;animation:pulse 1.5s ease-in-out infinite;"></div>`
    : "";

  return `
    <div style="display:flex;flex-direction:column;align-items:center;position:relative;">
      ${selectedRing}
      <div style="width:32px;height:32px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3);position:relative;z-index:2;${isSelected ? "transform:scale(1.1);" : ""}"></div>
      <div style="width:4px;height:16px;background:linear-gradient(to bottom,${color},${gradientEnd});border-radius:0 0 2px 2px;margin-top:-2px;box-shadow:0 2px 4px rgba(0,0,0,0.2);"></div>
    </div>
  `;
}

function createMarkerElement(
  color: string,
  isSelected = false,
): HTMLDivElement {
  const el = document.createElement("div");
  el.style.cursor = "pointer";
  el.dataset.markerColor = color;
  el.innerHTML = getMarkerInnerHTML(color, isSelected);
  return el;
}

function updateMarkerAppearance(
  el: HTMLElement,
  color: string,
  isSelected: boolean,
): void {
  el.innerHTML = getMarkerInnerHTML(color, isSelected);
}

function injectPulseAnimation(): void {
  if (document.getElementById("marker-pulse-style")) return;
  const style = document.createElement("style");
  style.id = "marker-pulse-style";
  style.textContent = `
    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 0.5; }
      50% { transform: scale(1.2); opacity: 0.3; }
    }
  `;
  document.head.appendChild(style);
}

interface UseMapOptions {
  onOriginChange?: (location: Location) => void;
  onDestinationChange?: (location: Location) => void;
  onStationClick?: (station: Station) => void;
  onEventClick?: (event: TrotroEvent) => void;
  onEventHover?: (event: TrotroEvent | null) => void;
  onMapReady?: (center: Location) => void;
  onMapClick?: () => void;
  externalMap?: maplibregl.Map | null;
}

export function useMap(options: UseMapOptions = {}) {
  const { externalMap } = options;
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const originMarker = useRef<maplibregl.Marker | null>(null);
  const destinationMarker = useRef<maplibregl.Marker | null>(null);
  const routeLayerIds = useRef<string[]>([]);
  const antAnimationFrame = useRef<number | null>(null);
  const isMapReady = useRef(false);
  const optionsRef = useRef(options);
  const pendingDestination = useRef<Location | null | undefined>(undefined);
  const pendingRoute = useRef<RouteOption | null | undefined>(undefined);
  const selectedMarker = useRef<MarkerType>(null);
  const originLocation = useRef<{ lng: number; lat: number }>({
    lng: ACCRA_CENTER[0],
    lat: ACCRA_CENTER[1],
  });
  const destinationLocation = useRef<{ lng: number; lat: number } | null>(null);
  const stationsLoaded = useRef(false);
  const pendingStations = useRef<Station[] | undefined>(undefined);
  const stationMarkers = useRef<Map<string, StationMarkerData>>(new Map());
  const selectedStationIdRef = useRef<string | null>(null);
  const eventMarkers = useRef<Map<string, EventMarkerData>>(new Map());
  const selectedEventIdRef = useRef<string | null>(null);
  const currentZoom = useRef(DEFAULT_ZOOM);
  const stationVisibilityRaf = useRef<number | null>(null);
  const pendingEvents = useRef<TrotroEvent[] | undefined>(undefined);
  const eventsVisible = useRef(true);
  const hasNotifiedReady = useRef(false);
  const currentMapStyle = useRef<MapStyleName>("standard");
  const currentDestination = useRef<Location | null>(null);
  const currentRoute = useRef<RouteOption | null>(null);
  const lastRouteBounds = useRef<maplibregl.LngLatBounds | null>(null);
  const currentStations = useRef<Station[]>([]);
  const currentEvents = useRef<TrotroEvent[]>([]);

  const [eventsState, setEventsState] = useState<TrotroEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [stationsState, setStationsState] = useState<Station[]>([]);
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const [routeState, setRouteState] = useState<RouteOption | null>(null);
  const [compactMode, setCompactMode] = useState(false);
  const [boundsState, setBoundsState] = useState<maplibregl.LngLatBounds | null>(null);
  const [stationVisibility, setStationVisibility] = useState<Map<string, { visible: boolean; showLabel: boolean }>>(new Map());
  const [trackedZoom, setTrackedZoom] = useState(DEFAULT_ZOOM);

  const stationsRef = useRef<Station[]>([]);
  stationsRef.current = stationsState;

  optionsRef.current = options;

  const isCompactMode = useCallback(() => {
    return currentZoom.current < COMPACT_PIN_ZOOM_THRESHOLD;
  }, []);

  const renderStationPinIfNeeded = useCallback(
    (
      data: StationMarkerData,
      next: { isSelected: boolean; showLabel: boolean; isVisible: boolean },
    ) => {
      const prev = data.renderState;
      if (
        prev.isSelected === next.isSelected
        && prev.showLabel === next.showLabel
        && prev.isVisible === next.isVisible
      ) {
        return;
      }

      renderStationPin(
        data.root,
        data.station,
        next.isSelected,
        data.onClick,
        next.showLabel,
        next.isVisible,
      );
      data.renderState = next;
    },
    [],
  );

  const updateStationVisibilityForZoom = useCallback(() => {
    const mapInstance = map.current;
    if (!mapInstance || !isMapReady.current) return;

    const stations = stationsRef.current;
    if (stations.length === 0) return;

    const zoom = currentZoom.current;
    const visible = zoom >= STATION_MIN_ZOOM;
    const bounds = mapInstance.getBounds();
    const occupiedCells = new Set<string>();
    const occupiedLabelCells = new Set<string>();
    const gridSize = getStationGridSize(zoom);
    const labelGrid = Math.max(64, Math.round(gridSize * 1.5));
    const next = new Map<string, { visible: boolean; showLabel: boolean }>();
    const selectedId = selectedStationIdRef.current;

    stations.forEach((station) => {
      const isSelected = station.id === selectedId;
      const inBounds = bounds.contains([station.lon, station.lat]);

      if ((!visible && !isSelected) || (!inBounds && !isSelected)) {
        next.set(station.id, {
          visible: false,
          showLabel: shouldShowStationLabel(zoom, false),
        });
        return;
      }

      const point = mapInstance.project([station.lon, station.lat]);
      const cellKey = `${Math.floor(point.x / gridSize)}:${Math.floor(point.y / gridSize)}`;
      const canShow = isSelected || !occupiedCells.has(cellKey);

      if (!canShow) {
        next.set(station.id, {
          visible: false,
          showLabel: shouldShowStationLabel(zoom, false),
        });
        return;
      }

      occupiedCells.add(cellKey);

      let showLabel = shouldShowStationLabel(zoom, isSelected);
      if (!isSelected && showLabel) {
        const labelCellKey = `${Math.floor(point.x / labelGrid)}:${Math.floor(point.y / labelGrid)}`;
        if (occupiedLabelCells.has(labelCellKey)) {
          showLabel = false;
        } else {
          occupiedLabelCells.add(labelCellKey);
        }
      }

      next.set(station.id, { visible: true, showLabel });
    });

    setStationVisibility(next);
  }, []);

  const scheduleStationVisibilityUpdate = useCallback(() => {
    if (stationVisibilityRaf.current !== null) return;

    stationVisibilityRaf.current = requestAnimationFrame(() => {
      stationVisibilityRaf.current = null;
      updateStationVisibilityForZoom();
    });
  }, [updateStationVisibilityForZoom]);

  useEffect(() => {
    if (stationsState.length > 0 && isMapReady.current && map.current) {
      scheduleStationVisibilityUpdate();
    }
  }, [stationsState, scheduleStationVisibilityUpdate]);

  const clearRouteLayers = useCallback(() => {
    const mapInstance = map.current;
    if (!mapInstance || !isMapReady.current) return;

    if (antAnimationFrame.current !== null) {
      cancelAnimationFrame(antAnimationFrame.current);
      antAnimationFrame.current = null;
    }

    try {
      if (!mapInstance.isStyleLoaded()) return;

      const sourceIds = new Set<string>();

      for (const id of routeLayerIds.current) {
        if (mapInstance.getLayer(id)) {
          mapInstance.removeLayer(id);
        }
        const sourceId = id.replace(/-border$|-main$|-dot$|-ant$/, "");
        sourceIds.add(sourceId);
      }

      for (const sourceId of sourceIds) {
        if (mapInstance.getSource(sourceId)) {
          mapInstance.removeSource(sourceId);
        }
      }
    } catch (e) {
      console.error("Error clearing route layers:", e);
    }

    routeLayerIds.current = [];
  }, []);

  const updateMarkerSelectionState = useCallback((markerType: MarkerType) => {
    selectedMarker.current = markerType;

    if (originMarker.current) {
      const el = originMarker.current.getElement();
      if (el) {
        updateMarkerAppearance(el, "#3b82f6", markerType === "origin");
      }
    }

    if (destinationMarker.current) {
      const el = destinationMarker.current.getElement();
      if (el) {
        updateMarkerAppearance(el, "#ef4444", markerType === "destination");
      }
    }
  }, []);

  const handleMarkerClick = useCallback(
    (clickedMarker: "origin" | "destination", e: globalThis.Event) => {
      e.stopPropagation();
      const newSelection =
        selectedMarker.current === clickedMarker ? null : clickedMarker;
      updateMarkerSelectionState(newSelection);
    },
    [updateMarkerSelectionState],
  );

  const fitBoundsToMarkers = useCallback(() => {
    const mapInstance = map.current;
    if (!mapInstance || !isMapReady.current) return;
    if (!originLocation.current || !destinationLocation.current) return;

    const bounds = new (getMaplibre().LngLatBounds)();
    bounds.extend([originLocation.current.lng, originLocation.current.lat]);
    bounds.extend([
      destinationLocation.current.lng,
      destinationLocation.current.lat,
    ]);

    const isMobile =
      typeof window !== "undefined" && window.innerWidth < 768;

    mapInstance.fitBounds(bounds, {
      padding: isMobile
        ? {
            top: 80,
            bottom: getBottomSheetBottomInset() ?? 420,
            left: 40,
            right: 40,
          }
        : { top: 120, bottom: 180, left: 100, right: 100 },
      maxZoom: 15,
    });
  }, []);

  const applyDestination = useCallback(
    (location: Location | null) => {
      const mapInstance = map.current;
      if (!mapInstance || !isMapReady.current) return;

      currentDestination.current = location;

      try {
        if (!mapInstance.isStyleLoaded()) {
          pendingDestination.current = location;
          return;
        }

        if (
          location &&
          typeof location.longitude === "number" &&
          typeof location.latitude === "number"
        ) {
          destinationLocation.current = {
            lng: location.longitude,
            lat: location.latitude,
          };

          if (!destinationMarker.current) {
            const el = createMarkerElement("#ef4444", false);
            el.addEventListener("click", (e) =>
              handleMarkerClick("destination", e),
            );

            destinationMarker.current = new (getMaplibre().Marker)({
              element: el,
              anchor: "bottom",
            })
              .setLngLat([location.longitude, location.latitude])
              .addTo(mapInstance);
          } else {
            destinationMarker.current.setLngLat([
              location.longitude,
              location.latitude,
            ]);
          }

          fitBoundsToMarkers();
        } else {
          destinationLocation.current = null;
          if (destinationMarker.current) {
            destinationMarker.current.remove();
            destinationMarker.current = null;
          }
          if (selectedMarker.current === "destination") {
            selectedMarker.current = null;
          }
        }
      } catch (e) {
        console.error("Error applying destination:", e);
      }
    },
    [handleMarkerClick, fitBoundsToMarkers],
  );

  const applyRoute = useCallback(
    (route: RouteOption | null) => {
      const isExternal = optionsRef.current.externalMap !== undefined;

      setRouteState(route);

      if (isExternal) {
        return;
      }

      const mapInstance = map.current;
      if (!mapInstance || !isMapReady.current) return;

      try {
        if (!mapInstance.isStyleLoaded()) return;

        clearRouteLayers();

        if (!route || route.steps.length === 0) return;

        const bounds = new (getMaplibre().LngLatBounds)();
        const newLayerIds: string[] = [];

        route.steps.forEach((step, index) => {
          if (
            !step.coordinates ||
            step.coordinates.length === 0 ||
            !map.current
          )
            return;

          const sourceId = `route-segment-${index}`;
          const borderLayerId = `${sourceId}-border`;
          const mainLayerId = `${sourceId}-main`;
          const isWalking = step.type === "walking";
          const lineColor = step.color || (isWalking ? "#EDAB3A" : "#2EC4B6");

          map.current.addSource(sourceId, {
            type: "geojson",
            data: {
              type: "Feature",
              properties: {},
              geometry: {
                type: "LineString",
                coordinates: step.coordinates,
              },
            },
          });

          map.current.addLayer({
            id: borderLayerId,
            type: "line",
            source: sourceId,
            layout: { "line-join": "round", "line-cap": "round" },
            paint: {
              "line-color": "#C8DCF0",
              "line-width": 8,
              "line-opacity": 1,
            },
          });

          map.current.addLayer({
            id: mainLayerId,
            type: "line",
            source: sourceId,
            layout: { "line-join": "round", "line-cap": "round" },
            paint: {
              "line-color": lineColor,
              "line-width": 4,
              "line-opacity": 1,
              ...(isWalking ? { "line-dasharray": [2, 2] } : {}),
            },
          });

          const isInformal = !isWalking && step.scheduled === false;
          if (isInformal) {
            const antLayerId = `${sourceId}-ant`;
            map.current.addLayer({
              id: antLayerId,
              type: "line",
              source: sourceId,
              layout: { "line-join": "round", "line-cap": "round" },
              paint: {
                "line-color": "#ffffff",
                "line-width": 6,
                "line-dasharray": [1, 3],
                "line-opacity": 1,
              },
            });
            newLayerIds.push(antLayerId);
          }

          newLayerIds.push(borderLayerId, mainLayerId);

          for (const [lng, lat] of step.coordinates) {
            if (typeof lng === "number" && typeof lat === "number") {
              bounds.extend([lng, lat]);
            }
          }
        });

        route.steps.forEach((step, index) => {
          if (!map.current) return;
          const nextStep = route.steps[index + 1];
          if (!nextStep) return;

          const showDot =
            (step.type === "trotro" &&
              (nextStep.type === "trotro" || nextStep.type === "walking")) ||
            (step.type === "walking" && nextStep.type === "trotro");
          if (!showDot) return;

          const coords = step.coordinates;
          if (!coords || coords.length === 0) return;
          const lastCoord = coords[coords.length - 1];
          const dotColor = step.color || "#2EC4B6";

          const dotSourceId = `transfer-dot-${index}`;
          const dotLayerId = `${dotSourceId}-dot`;

          map.current.addSource(dotSourceId, {
            type: "geojson",
            data: {
              type: "Feature",
              properties: { color: dotColor },
              geometry: {
                type: "Point",
                coordinates: lastCoord,
              },
            },
          });

          map.current.addLayer({
            id: `${dotSourceId}-border`,
            type: "circle",
            source: dotSourceId,
            paint: {
              "circle-radius": 7,
              "circle-color": "#ffffff",
            },
          });

          map.current.addLayer({
            id: dotLayerId,
            type: "circle",
            source: dotSourceId,
            paint: {
              "circle-radius": 5,
              "circle-color": dotColor,
            },
          });

          newLayerIds.push(`${dotSourceId}-border`, dotLayerId);
        });

        routeLayerIds.current = newLayerIds;

        const antLayerIds = newLayerIds.filter((id) => id.endsWith("-ant"));
        if (antLayerIds.length > 0 && antAnimationFrame.current === null) {
          let step = 0;
          function animateAnts(timestamp: number) {
            const mapInstance = map.current;
            if (!mapInstance) return;
            const newStep = Math.floor(
              (timestamp / 50) % ANT_DASH_SEQUENCE.length,
            );
            if (newStep !== step) {
              step = newStep;
              const dash = ANT_DASH_SEQUENCE[step];
              for (const layerId of antLayerIds) {
                try {
                  if (mapInstance.getLayer(layerId)) {
                    mapInstance.setPaintProperty(
                      layerId,
                      "line-dasharray",
                      dash,
                    );
                  }
                } catch {
                  /* layer may have been removed */
                }
              }
            }
            antAnimationFrame.current = requestAnimationFrame(animateAnts);
          }
          antAnimationFrame.current = requestAnimationFrame(animateAnts);
        }

        if (!bounds.isEmpty() && map.current) {
          lastRouteBounds.current = bounds;
          const isMobile =
            typeof window !== "undefined" && window.innerWidth < 768;
          map.current.fitBounds(bounds, {
            padding: isMobile
              ? {
                  top: 60,
                  bottom: getBottomSheetBottomInset() ?? 360,
                  left: 40,
                  right: 40,
                }
              : 80,
            maxZoom: 15,
          });
        }
      } catch (e) {
        console.error("Error applying route:", e);
      }
    },
    [clearRouteLayers],
  );

  const applyStations = useCallback((stations: Station[]) => {
    const isExternal = optionsRef.current.externalMap !== undefined;

    setStationsState(stations);

    if (isExternal) {
      return;
    }

    const mapInstance = map.current;
    if (!mapInstance || !isMapReady.current) return;

    try {
      if (!mapInstance.isStyleLoaded()) return;

      stationMarkers.current.forEach(({ root }) => {
        root.unmount();
      });
      stationMarkers.current.forEach(({ marker }) => {
        marker.remove();
      });
      stationMarkers.current.clear();

      if (stations.length === 0) return;

      stations.forEach((station) => {
        const isSelected = station.id === selectedStationIdRef.current;
        const element = document.createElement("div");
        element.className = "station-pin-container";
        element.style.cssText = "cursor: pointer; touch-action: manipulation;";

        const root = createRoot(element);

        const handleClick = () => {
          selectedStationIdRef.current = station.id;

          stationMarkers.current.forEach((data) => {
            const selected = data.station.id === station.id;
            renderStationPinIfNeeded(data, {
              isSelected: selected,
              showLabel: shouldShowStationLabel(currentZoom.current, selected),
              isVisible: true,
            });
          });

          optionsRef.current.onStationClick?.(station);
        };

        renderStationPin(root, station, isSelected, handleClick, false, false);

        const marker = new (getMaplibre().Marker)({
          element,
          anchor: "bottom",
        })
          .setLngLat([station.lon, station.lat])
          .addTo(mapInstance);

        stationMarkers.current.set(station.id, {
          marker,
          station,
          element,
          root,
          onClick: handleClick,
          renderState: {
            isSelected,
            showLabel: false,
            isVisible: false,
          },
        });
      });

      updateStationVisibilityForZoom();

      stationsLoaded.current = true;
    } catch (e) {
      console.error("Error applying stations:", e);
    }
  }, [renderStationPinIfNeeded, updateStationVisibilityForZoom]);

  const setSelectedStation = useCallback((stationId: string | null) => {
    selectedStationIdRef.current = stationId;
    setSelectedStationId(stationId);

    stationMarkers.current.forEach((data) => {
      const isSelected = data.station.id === stationId;
      renderStationPinIfNeeded(data, {
        isSelected,
        showLabel: shouldShowStationLabel(currentZoom.current, isSelected),
        isVisible: true,
      });
    });
    scheduleStationVisibilityUpdate();
  }, [renderStationPinIfNeeded, scheduleStationVisibilityUpdate]);

  const clearEventMarkers = useCallback(() => {
    eventMarkers.current.forEach(({ marker }) => {
      marker.remove();
    });
    eventMarkers.current.clear();
  }, []);

  const createMarkerForEvent = useCallback(
    (event: TrotroEvent, isSelected: boolean) => {
      const mapInstance = map.current;
      if (!mapInstance) return null;

      const isCompact = isCompactMode();
      const element = createEventPinElement(
        event,
        isSelected,
        isCompact,
        () => {
          optionsRef.current.onEventClick?.(event);
        },
      );

      element.addEventListener("mouseenter", () => {
        optionsRef.current.onEventHover?.(event);
      });
      element.addEventListener("mouseleave", () => {
        optionsRef.current.onEventHover?.(null);
      });

      const marker = new (getMaplibre().Marker)({
        element,
        anchor: isCompact ? "center" : "bottom",
        offset: isCompact ? [0, 0] : [0, 8],
      })
        .setLngLat([event.lon, event.lat])
        .addTo(mapInstance);

      return { marker, event, element, isCompact };
    },
    [isCompactMode],
  );

  const applyEvents = useCallback(
    (events: TrotroEvent[]) => {
      const isExternal = optionsRef.current.externalMap !== undefined;

      setEventsState(events);

      if (isExternal) {
        return;
      }

      const mapInstance = map.current;
      if (!mapInstance || !isMapReady.current) return;

      try {
        if (!mapInstance.isStyleLoaded()) return;

        clearEventMarkers();

        if (events.length === 0 || !eventsVisible.current) return;

        events.forEach((event) => {
          if (!event.lat || !event.lon) return;

          const isSelected = event.id === selectedEventIdRef.current;
          const markerData = createMarkerForEvent(event, isSelected);

          if (markerData) {
            eventMarkers.current.set(event.id, markerData);
          }
        });
      } catch (e) {
        console.error("Error applying events:", e);
      }
    },
    [clearEventMarkers, createMarkerForEvent],
  );

  const updateMarkersForZoom = useCallback(() => {
    const isCompact = isCompactMode();

    eventMarkers.current.forEach((data, eventId) => {
      if (data.isCompact !== isCompact) {
        const { marker, event } = data;
        const isSelected = eventId === selectedEventIdRef.current;

        marker.remove();

        const newMarkerData = createMarkerForEvent(event, isSelected);
        if (newMarkerData) {
          eventMarkers.current.set(eventId, newMarkerData);
        }
      }
    });
  }, [isCompactMode, createMarkerForEvent]);

  useEffect(() => {
    const isExternal = externalMap !== undefined;

    if (isExternal) {
      if (!externalMap || map.current) return;
    } else {
      if (!mapContainer.current || map.current) return;
    }

    let cancelled = false;
    let cleanup: (() => void) | null = null;

    void (async () => {
      await loadMaplibre();
      if (cancelled) return;

      const ML = getMaplibre();
      let mapInstance: maplibregl.Map;

      if (isExternal) {
        mapInstance = externalMap;
        map.current = mapInstance;
      } else {
        injectPulseAnimation();

        mapInstance = new ML.Map({
          container: mapContainer.current!,
          style: resolveMapStyle(currentMapStyle.current),
          center: ACCRA_CENTER,
          attributionControl: false,
          zoom: DEFAULT_ZOOM,
        });

        map.current = mapInstance;
      }

      const originEl = createMarkerElement("#3b82f6", false);
      originEl.addEventListener("click", (e) => handleMarkerClick("origin", e));

      originMarker.current = new ML.Marker({
      element: originEl,
      anchor: "bottom",
    })
      .setLngLat(ACCRA_CENTER)
      .addTo(mapInstance);

    const handleMapMove = () => {
      if (!mapInstance || !selectedMarker.current) return;

      const center = mapInstance.getCenter();

      if (selectedMarker.current === "origin" && originMarker.current) {
        originMarker.current.setLngLat([center.lng, center.lat]);
        originLocation.current = { lng: center.lng, lat: center.lat };
      } else if (
        selectedMarker.current === "destination" &&
        destinationMarker.current
      ) {
        destinationMarker.current.setLngLat([center.lng, center.lat]);
        destinationLocation.current = { lng: center.lng, lat: center.lat };
      }
    };

    const handleMoveEnd = () => {
      if (mapInstance && selectedMarker.current) {
        const center = mapInstance.getCenter();

        if (selectedMarker.current === "origin") {
          optionsRef.current.onOriginChange?.({
            latitude: center.lat,
            longitude: center.lng,
            name: "Selected Location",
          });
        } else if (selectedMarker.current === "destination") {
          optionsRef.current.onDestinationChange?.({
            latitude: center.lat,
            longitude: center.lng,
            name: "Selected Location",
          });
        }
      }

      scheduleStationVisibilityUpdate();
      const newBounds = mapInstance.getBounds();
      setBoundsState((prev) => {
        if (!prev) return newBounds;
        const prevArr = prev.toArray();
        const newArr = newBounds.toArray();
        if (prevArr[0] === newArr[0] && prevArr[1] === newArr[1]) return prev;
        return newBounds;
      });
      const z = mapInstance.getZoom();
      setTrackedZoom((prev) => (Math.abs(prev - z) < 0.01 ? prev : z));
    };

    const handleStyleLoad = () => {
      isMapReady.current = true;

      if (!hasNotifiedReady.current) {
        const center = mapInstance.getCenter();
        optionsRef.current.onMapReady?.({
          latitude: center.lat,
          longitude: center.lng,
        });
        hasNotifiedReady.current = true;
      }

      queueMicrotask(() => {
        applyDestination(currentDestination.current);
        applyRoute(currentRoute.current);
        applyStations(currentStations.current);
        if (eventsVisible.current) {
          applyEvents(currentEvents.current);
        }
      });

      if (pendingDestination.current !== undefined) {
        queueMicrotask(() => {
          applyDestination(pendingDestination.current!);
          pendingDestination.current = undefined;
        });
      }

      if (pendingRoute.current !== undefined) {
        queueMicrotask(() => {
          applyRoute(pendingRoute.current!);
          pendingRoute.current = undefined;
        });
      }

      if (pendingStations.current !== undefined) {
        queueMicrotask(() => {
          applyStations(pendingStations.current!);
          pendingStations.current = undefined;
        });
      }

      if (pendingEvents.current !== undefined) {
        queueMicrotask(() => {
          applyEvents(pendingEvents.current!);
          pendingEvents.current = undefined;
        });
      }
      scheduleStationVisibilityUpdate();
    };

    const handleZoom = () => {
      const zoom = mapInstance.getZoom();
      const wasCompact = currentZoom.current < COMPACT_PIN_ZOOM_THRESHOLD;
      const isNowCompact = zoom < COMPACT_PIN_ZOOM_THRESHOLD;

      currentZoom.current = zoom;
      setTrackedZoom((prev) => (Math.abs(prev - zoom) < 0.01 ? prev : zoom));
      setCompactMode(isNowCompact);
      setBoundsState(mapInstance.getBounds());
      scheduleStationVisibilityUpdate();

      if (wasCompact !== isNowCompact) {
        updateMarkersForZoom();
      }
    };

    const handleMapClick = (e: maplibregl.MapMouseEvent) => {
      const target = e.originalEvent?.target;
      if (target instanceof Element) {
        if (target.closest(".station-pin-container")) return;
        if (target.closest(".event-marker")) return;
        if (target.closest(".maplibregl-marker")) return;
      }

      if (!selectedStationIdRef.current) return;
      setSelectedStation(null);
      optionsRef.current.onMapClick?.();
    };

    if (mapInstance.isStyleLoaded()) {
      handleStyleLoad();
    }

    mapInstance.on("move", handleMapMove);
    mapInstance.on("moveend", handleMoveEnd);
    mapInstance.on("load", handleStyleLoad);
    mapInstance.on("zoom", handleZoom);
    mapInstance.on("click", handleMapClick);

    cleanup = () => {
      isMapReady.current = false;
      routeLayerIds.current = [];
      if (antAnimationFrame.current !== null) {
        cancelAnimationFrame(antAnimationFrame.current);
        antAnimationFrame.current = null;
      }
      stationsLoaded.current = false;
      if (stationVisibilityRaf.current !== null) {
        cancelAnimationFrame(stationVisibilityRaf.current);
        stationVisibilityRaf.current = null;
      }
      pendingDestination.current = undefined;
      pendingRoute.current = undefined;
      pendingStations.current = undefined;
      pendingEvents.current = undefined;
      selectedMarker.current = null;
      selectedStationIdRef.current = null;
      selectedEventIdRef.current = null;
      hasNotifiedReady.current = false;
      originMarker.current?.remove();
      originMarker.current = null;
      destinationMarker.current?.remove();
      destinationMarker.current = null;
      stationMarkers.current.forEach(({ root }) => {
        root.unmount();
      });
      stationMarkers.current.forEach(({ marker }) => {
        marker.remove();
      });
      stationMarkers.current.clear();
      clearEventMarkers();
      mapInstance.off("move", handleMapMove);
      mapInstance.off("moveend", handleMoveEnd);
      mapInstance.off("load", handleStyleLoad);
      mapInstance.off("zoom", handleZoom);
      mapInstance.off("click", handleMapClick);
      if (!isExternal) {
        mapInstance.remove();
      }
      map.current = null;
    };
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [
    externalMap,
    applyDestination,
    applyRoute,
    applyStations,
    applyEvents,
    handleMarkerClick,
    clearEventMarkers,
    scheduleStationVisibilityUpdate,
    updateMarkersForZoom,
  ]);

  const setDestination = useCallback(
    (location: Location | null) => {
      currentDestination.current = location;

      if (!map.current) {
        pendingDestination.current = location;
        return;
      }

      queueMicrotask(() => {
        if (isMapReady.current && map.current?.isStyleLoaded()) {
          applyDestination(location);
        } else {
          pendingDestination.current = location;
        }
      });
    },
    [applyDestination],
  );

  const refitRoute = useCallback(() => {
    const mapInstance = map.current;
    const bounds = lastRouteBounds.current;
    if (!mapInstance || !isMapReady.current || !bounds || bounds.isEmpty()) {
      return;
    }
    const isMobile =
      typeof window !== "undefined" && window.innerWidth < 768;
    mapInstance.fitBounds(bounds, {
      padding: isMobile
        ? {
            top: 60,
            bottom: getBottomSheetBottomInset() ?? 360,
            left: 40,
            right: 40,
          }
        : 80,
      maxZoom: 15,
    });
  }, []);

  const renderRoute = useCallback(
    (route: RouteOption | null) => {
      currentRoute.current = route;

      if (!map.current) return;

      queueMicrotask(() => {
        if (isMapReady.current && map.current?.isStyleLoaded()) {
          applyRoute(route);
        } else {
          pendingRoute.current = route;
        }
      });
    },
    [applyRoute],
  );

  const setOrigin = useCallback((location: Location) => {
    if (!map.current || !originMarker.current) return;

    queueMicrotask(() => {
      if (originMarker.current && map.current) {
        originLocation.current = {
          lng: location.longitude,
          lat: location.latitude,
        };
        originMarker.current.setLngLat([location.longitude, location.latitude]);
      }
    });
  }, []);

  const flyTo = useCallback((location: Location, zoom = 14) => {
    const mapInstance = map.current;
    if (!mapInstance) return;

    mapInstance.flyTo({
      center: [location.longitude, location.latitude],
      zoom,
    });
  }, []);

  const loadStations = useCallback(
    (stations: Station[]) => {
      currentStations.current = stations;

      if (!map.current) return;

      queueMicrotask(() => {
        if (isMapReady.current && map.current?.isStyleLoaded()) {
          applyStations(stations);
        } else {
          pendingStations.current = stations;
        }
      });
    },
    [applyStations],
  );

  const zoomIn = useCallback(() => map.current?.zoomIn(), []);
  const zoomOut = useCallback(() => map.current?.zoomOut(), []);

  const loadEvents = useCallback(
    (events: TrotroEvent[]) => {
      currentEvents.current = events;

      if (!map.current) return;

      queueMicrotask(() => {
        if (isMapReady.current && map.current?.isStyleLoaded()) {
          applyEvents(events);
        } else {
          pendingEvents.current = events;
        }
      });
    },
    [applyEvents],
  );

  const selectEvent = useCallback((eventId: string | null) => {
    const previousId = selectedEventIdRef.current;
    selectedEventIdRef.current = eventId;
    setSelectedEventId(eventId);

    if (previousId) {
      const prevData = eventMarkers.current.get(previousId);
      if (prevData) {
        updateEventPinElement(
          prevData.element,
          prevData.event,
          false,
          prevData.isCompact,
        );
      }
    }

    if (eventId) {
      const newData = eventMarkers.current.get(eventId);
      if (newData) {
        updateEventPinElement(
          newData.element,
          newData.event,
          true,
          newData.isCompact,
        );
      }
    }
  }, []);

  const flyToEvent = useCallback((event: TrotroEvent, zoom = 15) => {
    const mapInstance = map.current;
    if (!mapInstance || !event.lat || !event.lon) return;

    mapInstance.flyTo({
      center: [event.lon, event.lat],
      zoom,
      duration: 1000,
    });
  }, []);

  const setEventsVisible = useCallback(
    (visible: boolean) => {
      eventsVisible.current = visible;
      if (visible) {
        if (pendingEvents.current && pendingEvents.current.length > 0) {
          applyEvents(pendingEvents.current);
        }
      } else {
        clearEventMarkers();
      }
    },
    [applyEvents, clearEventMarkers],
  );

  const reloadEvents = useCallback(
    (events: TrotroEvent[]) => {
      currentEvents.current = events;
      pendingEvents.current = events;
      if (eventsVisible.current) {
        applyEvents(events);
      }
    },
    [applyEvents],
  );

  return {
    mapContainer,
    setDestination,
    setOrigin,
    renderRoute,
    refitRoute,
    loadStations,
    setSelectedStation,
    flyTo,
    zoomIn,
    zoomOut,
    loadEvents,
    selectEvent,
    flyToEvent,
    setEventsVisible,
    reloadEvents,
    clearEventMarkers,
    events: eventsState,
    selectedEventId: selectedEventId,
    stations: stationsState,
    selectedStationId: selectedStationId,
    route: routeState,
    compactMode,
    stationVisibility,
    viewportBounds: boundsState,
    currentZoom: trackedZoom,
    setMapStyle: (style: MapStyleName) => {
      currentMapStyle.current = style;
      if (!map.current || !isMapReady.current) return;
      map.current.setStyle(resolveMapStyle(style));
    },
  };
}
